


























































































import { Component, Prop, Vue } from 'vue-property-decorator';
import downloadService, { DownloadContentType } from '@/project/download/download.service';
import permissionService from '@/project/auth/permission.service';
import urlHelperService from '@/core/urlHelper.service';
import Api from '@/project/http/Api.service';
import CopyService from '@/core/copy/copy.service';
import CallToAction from '@/project/form/CallToAction.vue';
import { ProductTileViewObject } from '@/types/apiServerContract';
import bus from '@/core/bus';
import { GeneralErrorEventKey, ClientMessageType, ClientMessageWithError } from '@/core/messages/types';
import impersonationService from '@/project/shared/impersonation/impersonation.service';

@Component({
    components: { }
})
export default class SearchAdminOptions extends Vue {
    @Prop(String)
    private categoryId!: string;

    @Prop(Number)
    private pageSize!: number;

    @Prop(String)
    private campaignCode!: string;

    @Prop(String)
    private brandName!: string;

    @Prop(Array)
    private products!: ProductTileViewObject[] | undefined;

    private productIdsCopied = false;
    private productMPNsCopied = false;
    private productIdsCopiedLoading = false;
    private productExportLoading = false;
    private productExportIcecatLoading = false;
    private clearBoostCacheLoading = false;
    private boostCacheCleared = false;
    private rawResultDownloading = false;
    private searchExplanationDownloading = false;
    private showScore = urlHelperService.getShowScore();

    private get isStaff(): boolean {
        return permissionService.isStaff();
    }

    private get isImpersonating(): boolean {
        return impersonationService.isImpersonating;
    }

    private toggleShowScore() {
        this.showScore = !this.showScore;
        urlHelperService.setShowScore(this.showScore);
    }

    private async downloadRawResult(): Promise<void> {
        this.rawResultDownloading = true;

        try {
            const rawResult = await Api.search.raw({
                categoryId: this.categoryId || urlHelperService.getCategoryId() || '',
                categoryIds: urlHelperService.getCategoryIds(),
                term: urlHelperService.getTerm() ?? '',
                lastFacet: urlHelperService.getLastFacet() || '',
                page: urlHelperService.getPage(),
                pageSize: this.pageSize,
                filter: urlHelperService.getSearchFilterId(),
                priceSort: urlHelperService.getSearchPriceSortFilter(),
                campaignCode: this.campaignCode,
                brandName: this.brandName,
                showScore: urlHelperService.getShowScore(),
                explain: urlHelperService.getShowExplanation(),
                wIds: urlHelperService.getWidsFromTerm(),
                useFlattenedCategoryTree: false
            }, urlHelperService.getFacets());

            downloadService.downloadFile(rawResult, 'raw-result.json', DownloadContentType.JSON);
        } finally {
            this.rawResultDownloading = false;
        }
    }

    private async downloadExplanation(): Promise<void> {
        this.searchExplanationDownloading = true;

        try {
            const result = await Api.search.search({
                categoryId: this.categoryId || urlHelperService.getCategoryId() || '',
                categoryIds: urlHelperService.getCategoryIds(),
                term: urlHelperService.getTerm() ?? '',
                lastFacet: urlHelperService.getLastFacet() || '',
                page: urlHelperService.getPage(),
                pageSize: this.pageSize,
                filter: urlHelperService.getSearchFilterId(),
                priceSort: urlHelperService.getSearchPriceSortFilter(),
                campaignCode: this.campaignCode,
                brandName: this.brandName,
                showScore: urlHelperService.getShowScore(),
                explain: true,
                wIds: urlHelperService.getWidsFromTerm(),
                useFlattenedCategoryTree: false
            }, urlHelperService.getFacets(), 'admin');

            if (result.explanation) {
                downloadService.downloadFile(result.explanation, 'search-explanation.json', DownloadContentType.JSON);
            }
        } finally {
            this.searchExplanationDownloading = false;
        }
    }

    private async clearBoostCache(): Promise<void> {
        try {
            this.clearBoostCacheLoading = true;

            await Api.search.clearBoostCache();

            this.boostCacheCleared = true;

            setTimeout(() => {
                this.boostCacheCleared = false;
                this.$refs.cta.closeDropdown();
            }, 1500);
        } finally {
            this.clearBoostCacheLoading = false;
        }
    }

    private async copyMPNs() {
        this.productIdsCopiedLoading = true;
        try {
            let mpns: string[] = [];

            if (this.products) {
                mpns = this.products.filter(x => !!x.mpn).map(x => x.mpn) as string[];
            } else {
                const result = await Api.search.productIds({
                    categoryId: this.categoryId || urlHelperService.getCategoryId() || '',
                    categoryIds: urlHelperService.getCategoryIds(),
                    term: urlHelperService.getTerm() ?? '',
                    lastFacet: urlHelperService.getLastFacet() || '',
                    page: urlHelperService.getPage(),
                    pageSize: this.pageSize,
                    filter: urlHelperService.getSearchFilterId(),
                    priceSort: urlHelperService.getSearchPriceSortFilter(),
                    campaignCode: this.campaignCode,
                    brandName: this.brandName,
                    showScore: urlHelperService.getShowScore(),
                    explain: urlHelperService.getShowExplanation(),
                    wIds: urlHelperService.getWidsFromTerm(),
                    useFlattenedCategoryTree: false
                }, urlHelperService.getFacets());

                mpns = result.products.filter(x => !!x.mpn).map(x => x.mpn) as string[];
            }

            const text = mpns.join('\n');
            CopyService.copyTextToClipboard(text);

            this.productIdsCopiedLoading = false;
            this.productMPNsCopied = true;

            setTimeout(() => {
                this.productMPNsCopied = false;
                this.$refs.cta.closeDropdown();
            }, 1500);
        } finally {
            this.productIdsCopiedLoading = false;
        }
    }

    private async copyWIDs() {
        this.productIdsCopiedLoading = true;
        try {
            let productIds: string[] = [];

            if (this.products) {
                productIds = this.products.filter(x => !!x.wid).map(x => x.wid) as string[];
            } else {
                const result = await Api.search.productIds({
                    categoryId: this.categoryId || urlHelperService.getCategoryId() || '',
                    categoryIds: urlHelperService.getCategoryIds(),
                    term: urlHelperService.getTerm() ?? '',
                    lastFacet: urlHelperService.getLastFacet() || '',
                    page: urlHelperService.getPage(),
                    pageSize: this.pageSize,
                    filter: urlHelperService.getSearchFilterId(),
                    priceSort: urlHelperService.getSearchPriceSortFilter(),
                    campaignCode: this.campaignCode,
                    brandName: this.brandName,
                    showScore: urlHelperService.getShowScore(),
                    explain: urlHelperService.getShowExplanation(),
                    wIds: urlHelperService.getWidsFromTerm(),
                    useFlattenedCategoryTree: false
                }, urlHelperService.getFacets());

                productIds = result.products.filter(x => !!x.id).map(x => x.id) as string[];
            }

            const text = productIds.join('\n');
            CopyService.copyTextToClipboard(text);

            this.productIdsCopiedLoading = false;
            this.productIdsCopied = true;

            setTimeout(() => {
                this.productIdsCopied = false;
                this.$refs.cta.closeDropdown();
            }, 1500);
        } finally {
            this.productIdsCopiedLoading = false;
        }
    }

    private async requestProductExport(icecatMode: boolean) {
        if (icecatMode) {
            this.productExportIcecatLoading = true;
        } else {
            this.productExportLoading = true;
        }

        try {
            await Api.search.requestProductExport({
                categoryId: this.categoryId || urlHelperService.getCategoryId() || '',
                categoryIds: urlHelperService.getCategoryIds(),
                term: urlHelperService.getTerm() ?? '',
                lastFacet: urlHelperService.getLastFacet() || '',
                page: urlHelperService.getPage(),
                pageSize: this.pageSize,
                filter: urlHelperService.getSearchFilterId(),
                priceSort: urlHelperService.getSearchPriceSortFilter(),
                campaignCode: this.campaignCode,
                brandName: this.brandName,
                showScore: urlHelperService.getShowScore(),
                explain: urlHelperService.getShowExplanation(),
                wIds: urlHelperService.getWidsFromTerm(),
                useFlattenedCategoryTree: false
            },
            window.location.toString(),
            icecatMode,
            urlHelperService.getFacets());

            bus.emit(GeneralErrorEventKey, {
                errorId: '',
                messages: [{
                    message: 'Product.ExportButton.Success',
                    messageType: ClientMessageType.Info
                }]
            } as ClientMessageWithError);

            this.productExportLoading = false;
            this.productExportIcecatLoading = false;
        } finally {
            this.productExportLoading = false;
            this.productExportIcecatLoading = false;
        }
    }

    public $refs!: Vue['$refs'] & {
        cta: CallToAction
    }
}
